import { useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  Bell,
  BellOff,
  Check,
  CheckCheck,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  Image,
  Inbox,
  MapPin,
  MessageCircle,
  Mic,
  MoreVertical,
  Paperclip,
  Pencil,
  Plus,
  Reply,
  RotateCcw,
  Search,
  Send,
  Smile,
  Tag,
  Trash2,
  UserRound,
  Users,
  Video,
  WifiOff,
  X,
} from "lucide-react";
import {
  loadWhatsAppMessages,
  loadWhatsAppWorkspace,
  runWhatsAppAction,
  sendWhatsAppMessage,
  subscribeToWhatsApp,
} from "../services/whatsapp.js";

const money = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });
const initials = (value) =>
  (value || "?")
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
const digits = (value) => String(value || "").replace(/\D/g, "");

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 32 32" aria-hidden="true">
      <path
        fill="currentColor"
        d="M16.04 3A12.8 12.8 0 0 0 5.1 22.45L3.38 29l6.7-1.72A12.82 12.82 0 1 0 16.04 3Zm0 23.28a10.43 10.43 0 0 1-5.32-1.45l-.38-.23-3.98 1.02 1.06-3.87-.25-.4a10.45 10.45 0 1 1 8.87 4.93Zm5.73-7.82c-.31-.16-1.86-.92-2.15-1.02-.29-.1-.5-.16-.71.16-.21.31-.81 1.02-.99 1.23-.18.21-.37.24-.68.08-.31-.16-1.32-.49-2.51-1.55a9.43 9.43 0 0 1-1.74-2.17c-.18-.31-.02-.48.14-.64.14-.14.31-.37.47-.55.16-.18.21-.31.31-.52.1-.21.05-.39-.03-.55-.08-.16-.71-1.71-.97-2.34-.26-.62-.52-.54-.71-.55h-.61c-.21 0-.55.08-.84.39-.29.31-1.1 1.08-1.1 2.63s1.13 3.05 1.29 3.26c.16.21 2.22 3.39 5.38 4.75.75.32 1.34.52 1.8.66.76.24 1.44.21 1.99.13.61-.09 1.86-.76 2.12-1.5.26-.73.26-1.36.18-1.5-.08-.13-.29-.21-.6-.37Z"
      />
    </svg>
  );
}

const initialConversations = [
  {
    id: "carlos",
    name: "Carlos",
    phone: "+55 12 98319-5407",
    preview: "Vocês atendem o meu segmento?",
    time: "10:42",
    unread: 2,
    status: "open",
    owner: "Sidney Junior",
    online: true,
    archived: false,
    muted: false,
  },
  {
    id: "mariana",
    name: "Mariana Lopes",
    phone: "+55 21 99845-1020",
    preview: "Pode me enviar a proposta?",
    time: "09:18",
    unread: 1,
    status: "open",
    owner: "Sem responsável",
    online: false,
    archived: false,
    muted: false,
  },
  {
    id: "ricardo",
    name: "Ricardo Lima",
    phone: "+55 11 98877-2211",
    preview: "Obrigado pelo retorno!",
    time: "Ontem",
    unread: 0,
    status: "waiting",
    owner: "Sidney Junior",
    online: false,
    archived: false,
    muted: false,
  },
  {
    id: "camila",
    name: "Camila Souza",
    phone: "+55 41 99932-1180",
    preview: "Vamos marcar para quinta-feira.",
    time: "Ontem",
    unread: 0,
    status: "resolved",
    owner: "Juan Pinheiro",
    online: false,
    archived: false,
    muted: false,
  },
];

const initialMessages = {
  carlos: [
    {
      id: 1,
      direction: "in",
      type: "text",
      text: "Olá! Encontrei vocês no Google e gostaria de saber mais sobre gestão de tráfego.",
      time: "10:36",
    },
    {
      id: 2,
      direction: "out",
      type: "text",
      text: "Olá, Carlos! Tudo bem? Sou Sidney, da Caminho da Venda. Como podemos ajudar?",
      time: "10:38",
      status: "read",
    },
    {
      id: 3,
      direction: "in",
      type: "text",
      text: "Trabalho com um nicho mais restrito. Vocês atendem o meu segmento?",
      time: "10:42",
      reaction: "👍",
    },
  ],
  mariana: [
    {
      id: 1,
      direction: "in",
      type: "text",
      text: "Bom dia! Pode me enviar a proposta que conversamos?",
      time: "09:18",
    },
    {
      id: 2,
      direction: "out",
      type: "document",
      name: "Proposta-comercial.pdf",
      size: "1,8 MB",
      time: "09:22",
      status: "delivered",
    },
  ],
  ricardo: [
    {
      id: 1,
      direction: "out",
      type: "text",
      text: "Enviei o diagnóstico para seu e-mail, Ricardo.",
      time: "16:04",
      status: "read",
    },
    { id: 2, direction: "in", type: "audio", duration: "0:18", time: "16:10" },
  ],
  camila: [
    {
      id: 1,
      direction: "in",
      type: "location",
      text: "Local da reunião",
      address: "Av. Paulista, 1000 — São Paulo",
      time: "14:22",
    },
  ],
};

const initialLabels = [
  { id: "priority", name: "Prioridade", color: "#e6a83c" },
  { id: "proposal", name: "Proposta enviada", color: "#8a78e6" },
  { id: "waiting", name: "Aguardando resposta", color: "#399bd4" },
];

const serviceOptions = [
  "Gestão de Tráfego",
  "Consultoria Comercial",
  "Google Ads",
  "Meta Ads",
  "Criação de Landing Page",
  "Automação",
  "Outro",
];

function playNotification() {
  const AudioContext = window.AudioContext || window.webkitAudioContext;
  if (!AudioContext) return;
  const context = new AudioContext();
  const oscillator = context.createOscillator();
  const gain = context.createGain();
  oscillator.frequency.setValueAtTime(740, context.currentTime);
  oscillator.frequency.exponentialRampToValueAtTime(
    980,
    context.currentTime + 0.12,
  );
  gain.gain.setValueAtTime(0.0001, context.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.16, context.currentTime + 0.015);
  gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.22);
  oscillator.connect(gain);
  gain.connect(context.destination);
  oscillator.start();
  oscillator.stop(context.currentTime + 0.23);
  oscillator.onended = () => context.close();
}

function MessageStatus({ status }) {
  if (status === "failed")
    return <span className="message-failed">Falhou</span>;
  if (status === "sending") return <span className="message-clock">•••</span>;
  return <CheckCheck className={status === "read" ? "read" : ""} />;
}

function MessageContent({ message, onPreview }) {
  if (message.deletedAt)
    return <p className="deleted-message">Mensagem apagada</p>;
  if (message.type === "sticker")
    return message.mediaUrl ? (
      <img
        className="sticker-message"
        src={message.mediaUrl}
        alt="Figurinha"
        loading="lazy"
      />
    ) : (
      <p className="media-unavailable">[Figurinha indisponível]</p>
    );
  if (message.type === "document")
    return message.mediaUrl ? (
      <a className="document-message" href={message.mediaUrl} target="_blank" rel="noreferrer" download={message.name}>
        <FileText />
        <span><strong>{message.name || "Documento"}</strong><small>{message.size}</small></span>
        <Download />
      </a>
    ) : (
      <button className="document-message" onClick={() => onPreview(message)}>
        <FileText />
        <span>
          <strong>{message.name}</strong>
          <small>{message.size}</small>
        </span>
        <Download />
      </button>
    );
  if (message.type === "audio")
    return message.mediaUrl ? (
      <audio className="audio-player" controls preload="metadata" src={message.mediaUrl}>Seu navegador não suporta reprodução de áudio.</audio>
    ) : (
      <div className="audio-message">
        <button>
          <span />
        </button>
        <div>
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
          <i />
        </div>
        <small>{message.duration}</small>
      </div>
    );
  if (message.type === "image")
    return message.mediaUrl ? (
      <button className="received-image" onClick={() => onPreview(message)}>
        <img src={message.mediaUrl} alt={message.text || "Imagem recebida"} loading="lazy" />
      </button>
    ) : (
      <button className="image-message" onClick={() => onPreview(message)}>
        <Image />
        <span>Prévia de imagem</span>
      </button>
    );
  if (message.type === "video")
    return message.mediaUrl ? (
      <video className="video-player" controls preload="metadata" src={message.mediaUrl}>Seu navegador não suporta reprodução de vídeo.</video>
    ) : (
      <button
        className="image-message video"
        onClick={() => onPreview(message)}
      >
        <Video />
        <span>Prévia de vídeo</span>
      </button>
    );
  if (message.type === "location")
    return (
      <div className="location-message">
        <MapPin />
        <span>
          <strong>{message.text}</strong>
          <small>{message.address}</small>
        </span>
      </div>
    );
  return (
    <>
      {message.quoted && <div className="quoted-message">{message.quoted}</div>}
      <p>{message.text}</p>
    </>
  );
}

function ContactAvatar({ contact, className = "" }) {
  const [failedUrl, setFailedUrl] = useState("")
  const avatarUrl = contact?.avatarUrl || ""
  const showPhoto = Boolean(avatarUrl && failedUrl !== avatarUrl)

  return (
    <span
      className={`avatar ${className} ${showPhoto ? "has-photo" : ""}`}
    >
      {showPhoto ? (
        <img
          src={avatarUrl}
          alt={`Foto de ${contact.name}`}
          referrerPolicy="no-referrer"
          onError={() => setFailedUrl(avatarUrl)}
        />
      ) : (
        initials(contact?.name)
      )}
      {contact?.online && <i />}
    </span>
  );
}

export default function ServiceDesk({
  leads,
  stages,
  users,
  services,
  catalogLabels = [],
  organizationId,
  onOpenLead,
  onCreateOpportunity,
  onNewOpportunity,
  onUnreadChange,
  openConversationId,
}) {
  const [conversations, setConversations] = useState(
    organizationId ? [] : initialConversations,
  );
  const [selectedId, setSelectedId] = useState(organizationId ? "" : "carlos");
  const [messages, setMessages] = useState(
    organizationId ? {} : initialMessages,
  );
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [draft, setDraft] = useState("");
  const [showContact, setShowContact] = useState(true);
  const [headerMenu, setHeaderMenu] = useState(false);
  const [listMenu, setListMenu] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [connection, setConnection] = useState("connected");
  const [typing, setTyping] = useState(false);
  const [labels, setLabels] = useState(organizationId ? catalogLabels.filter((label) => label.active) : initialLabels);
  const [conversationLabels, setConversationLabels] = useState({
    carlos: ["priority"],
  });
  const [labelFilter, setLabelFilter] = useState("all");
  const [labelFilterMenu, setLabelFilterMenu] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [attachmentMenu, setAttachmentMenu] = useState(false);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [messageMenu, setMessageMenu] = useState(null);
  const [messageEdit, setMessageEdit] = useState(null);
  const [messageSaving, setMessageSaving] = useState(false);
  const [reactionPicker, setReactionPicker] = useState(null);
  const [replyTo, setReplyTo] = useState(null);
  const [requestedServices, setRequestedServices] = useState({
    carlos: "demo-service-0",
    ricardo: "demo-service-1",
    camila: "demo-service-1",
  });
  const [quickOpportunities, setQuickOpportunities] = useState({});
  const [quickConfirm, setQuickConfirm] = useState(false);
  const [quickStageId, setQuickStageId] = useState("");
  const [quickSaving, setQuickSaving] = useState(false);
  const [quickError, setQuickError] = useState("");
  const [privacyMode, setPrivacyMode] = useState(
    () => localStorage.getItem("cdv-service-privacy") === "1",
  );
  const [notice, setNotice] = useState("");
  const [loadingDesk, setLoadingDesk] = useState(Boolean(organizationId));
  const [mobileView, setMobileView] = useState("list");
  const messageAreaRef = useRef(null);
  const selectedIdRef = useRef(selectedId);

  const selected = conversations.find((item) => item.id === selectedId);
  const activeConversations = conversations.filter((item) =>
    filter === "archived" ? item.archived : !item.archived,
  );
  const visible = activeConversations.filter((item) => {
    const textMatch = `${item.name} ${item.phone} ${item.preview}`
      .toLowerCase()
      .includes(query.toLowerCase());
    const stateMatch =
      filter === "all" ||
      filter === "archived" ||
      (filter === "unread" && item.unread > 0) ||
      filter === item.status;
    const labelMatch =
      labelFilter === "all" ||
      (conversationLabels[item.id] || []).includes(labelFilter);
    return textMatch && stateMatch && labelMatch;
  });
  const relatedLeads = selected ? leads.filter((lead) =>
    lead.id === selected.leadId ||
    (selected.contactId && lead.whatsapp_contact_id === selected.contactId) ||
    digits(lead.phone_e164 || lead.phone) === digits(selected.phone)
  ) : [];
  const linkedLead = relatedLeads.find((lead) => stages.find((stage) => stage.id === lead.stage_id)?.kind === "open") || relatedLeads[0];
  const relationshipEstimated = relatedLeads.reduce((sum, lead) => sum + Number(lead.estimated_value || 0), 0);
  const relationshipWon = relatedLeads.reduce((sum, lead) => sum + Number(lead.won_value || 0), 0);
  const relationshipOpen = relatedLeads.filter((lead) => stages.find((stage) => stage.id === lead.stage_id)?.kind === "open").length;
  const opportunity =
    selected && (linkedLead || quickOpportunities[selected.id]);
  const selectedOwner =
    selected && users.find((user) => user.full_name === selected.owner);
  const unreadTotal = conversations
    .filter((item) => !item.archived)
    .reduce((sum, item) => sum + Number(item.unread || 0), 0);
  const archivedTotal = conversations.filter((item) => item.archived).length;

  function showArchivedConversations(show = true) {
    const target = show
      ? conversations.find((item) => item.archived)
      : conversations.find((item) => !item.archived);
    setFilter(show ? "archived" : "all");
    setSelectedId(target?.id || "");
    setMobileView("list");
    setListMenu(false);
    setContextMenu(null);
  }

  useEffect(() => onUnreadChange?.(unreadTotal), [unreadTotal, onUnreadChange]);
  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);
  useEffect(() => {
    if (!openConversationId || !conversations.some(item => item.id === openConversationId)) return;
    selectConversation(openConversationId);
  }, [openConversationId, conversations.length]);
  useEffect(() => {
    if (organizationId) setLabels(catalogLabels.filter((label) => label.active));
  }, [organizationId, catalogLabels]);
  useEffect(() => {
    localStorage.setItem("cdv-service-privacy", privacyMode ? "1" : "0");
  }, [privacyMode]);
  useEffect(() => {
    if (!organizationId) return;
    let active = true;
    async function refresh() {
      try {
        const workspace = await loadWhatsAppWorkspace(organizationId, users);
        if (!active) return;
        setConversations(workspace.conversations);
        setLabels(workspace.labels);
        setConversationLabels(workspace.conversationLabels);
        setConnection(
          workspace.channels.some((item) => item.status === "connected")
            ? "connected"
            : "disconnected",
        );
        setRequestedServices(
          Object.fromEntries(
            workspace.conversations.map((item) => [
              item.id,
              item.requestedServiceId || services.find(service => service.name === item.requestedService)?.id || "",
            ]),
          ),
        );
        setSelectedId((current) =>
          workspace.conversations.some((item) => item.id === current)
            ? current
            : workspace.conversations[0]?.id || "",
        );
      } catch (error) {
        if (active)
          setNotice(
            error.message || "Não foi possível carregar o atendimento.",
          );
      } finally {
        if (active) setLoadingDesk(false);
      }
    }
    refresh();
    const unsubscribe = subscribeToWhatsApp(organizationId, async (table, payload) => {
      const activeConversationId = selectedIdRef.current;
      if (
        activeConversationId &&
        ["whatsapp_messages", "whatsapp_message_attachments", "whatsapp_message_reactions"].includes(table)
      ) {
        const changedConversationId = payload?.new?.conversation_id || payload?.old?.conversation_id;
        const changedMessageId = payload?.new?.message_id || payload?.old?.message_id;
        const belongsToOpenConversation = !changedConversationId || changedConversationId === activeConversationId;
        const isRelatedMessageData = table !== "whatsapp_messages" && Boolean(changedMessageId);
        if (belongsToOpenConversation || isRelatedMessageData) {
          const rows = await loadWhatsAppMessages(activeConversationId);
          if (active && selectedIdRef.current === activeConversationId)
            setMessages((current) => ({ ...current, [activeConversationId]: rows }));
        }
      }
      await refresh();
    });
    return () => {
      active = false;
      unsubscribe();
    };
  }, [organizationId, users, services]);
  useEffect(() => {
    if (!organizationId || !selectedId) return;
    let active = true;
    loadWhatsAppMessages(selectedId)
      .then((rows) => {
        if (active)
          setMessages((current) => ({ ...current, [selectedId]: rows }));
      })
      .catch((error) => {
        if (active)
          setNotice(error.message || "Não foi possível carregar as mensagens.");
      });
    return () => {
      active = false;
    };
  }, [
    organizationId,
    selectedId,
    conversations.find((item) => item.id === selectedId)?.lastMessageAt,
  ]);
  useEffect(() => {
    if (!organizationId) return;
    const reconcileWhenVisible = () => {
      const activeConversationId = selectedIdRef.current;
      if (document.visibilityState !== "visible" || !activeConversationId) return;
      loadWhatsAppMessages(activeConversationId)
        .then((rows) => {
          if (selectedIdRef.current === activeConversationId)
            setMessages((current) => ({ ...current, [activeConversationId]: rows }));
        })
        .catch(() => {});
    };
    document.addEventListener("visibilitychange", reconcileWhenVisible);
    window.addEventListener("focus", reconcileWhenVisible);
    return () => {
      document.removeEventListener("visibilitychange", reconcileWhenVisible);
      window.removeEventListener("focus", reconcileWhenVisible);
    };
  }, [organizationId]);
  useEffect(() => {
    if (mobileView !== "chat" || !selectedId) return;
    const frame = requestAnimationFrame(() => {
      const area = messageAreaRef.current;
      if (area) area.scrollTop = area.scrollHeight;
    });
    return () => cancelAnimationFrame(frame);
  }, [mobileView, selectedId, messages[selectedId]?.length]);
  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") {
        setHeaderMenu(false);
        setListMenu(false);
        setContextMenu(null);
        setAttachmentMenu(false);
        setMediaPreview(null);
        setQuickConfirm(false);
        setTransferOpen(false);
        setLabelFilterMenu(false);
        setMessageMenu(null);
        setReactionPicker(null);
        setReplyTo(null);
      }
    };
    addEventListener("keydown", close);
    return () => removeEventListener("keydown", close);
  }, []);
  useEffect(() => {
    if (!notice) return;
    const timer = setTimeout(() => setNotice(""), 2600);
    return () => clearTimeout(timer);
  }, [notice]);

  const updateConversation = (id, changes) =>
    setConversations((current) =>
      current.map((item) => (item.id === id ? { ...item, ...changes } : item)),
    );
  async function selectConversation(id) {
    setSelectedId(id);
    setMobileView("chat");
    updateConversation(id, { unread: 0 });
    setHeaderMenu(false);
    if (organizationId)
      try {
        await runWhatsAppAction(id, "mark_read");
      } catch (error) {
        setNotice(error.message);
      }
  }

  function conversationCommercialMeta(conversation) {
    const lead = leads.find(
      (item) =>
        item.id === conversation.leadId ||
        digits(item.phone_e164 || item.phone) === digits(conversation.phone) ||
        item.name?.toLowerCase() === conversation.name?.toLowerCase(),
    );
    const stage = stages.find((item) => item.id === lead?.stage_id);
    return {
      firstName: conversation.name?.trim().split(/\s+/)[0] || "Contato",
      source: lead?.source_name || "WhatsApp",
      stage: stage?.name || (conversation.status === "resolved" ? "Finalizado" : "Em atendimento"),
    };
  }
  async function sendMessage(event) {
    event?.preventDefault();
    if (!selected || !draft.trim() || connection !== "connected") return;
    const text = draft.trim();
    if (organizationId) {
      setDraft("");
      const quotedId = replyTo?.id || null;
      setReplyTo(null);
      try {
        await sendWhatsAppMessage(selected.id, text, quotedId);
        const rows = await loadWhatsAppMessages(selected.id);
        setMessages((current) => ({ ...current, [selected.id]: rows }));
      } catch (error) {
        setDraft(text);
        setNotice(error.message || "Não foi possível enviar a mensagem.");
      }
      return;
    }
    const id = Date.now();
    const time = new Date().toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    });
    setMessages((current) => ({
      ...current,
      [selected.id]: [
        ...(current[selected.id] || []),
        {
          id,
          direction: "out",
          type: "text",
          text,
          time,
          status: "sending",
          quoted: replyTo?.text || replyTo?.name || null,
          quoted_message_id: replyTo?.id || null,
        },
      ],
    }));
    setDraft("");
    setReplyTo(null);
    updateConversation(selected.id, { preview: text, time });
    setTimeout(
      () =>
        setMessages((current) => ({
          ...current,
          [selected.id]: current[selected.id].map((item) =>
            item.id === id ? { ...item, status: "delivered" } : item,
          ),
        })),
      700,
    );
  }
  function simulateIncoming() {
    if (!selected) return;
    setListMenu(false);
    setTyping(true);
    setTimeout(() => {
      const message = {
        id: Date.now(),
        direction: "in",
        type: "text",
        text: "Olá! Esta é uma nova mensagem simulada para validar a notificação.",
        time: new Date().toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
      setMessages((current) => ({
        ...current,
        [selected.id]: [...(current[selected.id] || []), message],
      }));
      updateConversation(selected.id, {
        preview: message.text,
        time: message.time,
        unread: (selected.unread || 0) + 1,
      });
      setTyping(false);
      if (soundEnabled && !selected.muted) playNotification();
    }, 900);
  }
  async function archive(id, archived = true) {
    if (organizationId)
      try {
        await runWhatsAppAction(id, archived ? "archive" : "restore");
      } catch (error) {
        return setNotice(error.message);
      }
    updateConversation(id, { archived });
    setContextMenu(null);
    setHeaderMenu(false);
    setNotice(archived ? "Conversa arquivada." : "Conversa restaurada.");
    const next = conversations.find(
      (item) => item.id !== id && item.archived !== archived,
    );
    if (selectedId === id) setSelectedId(next?.id || "");
  }
  async function toggleConversationLabel(id, labelId) {
    const hasLabel = (conversationLabels[id] || []).includes(labelId);
    if (organizationId)
      try {
        await runWhatsAppAction(id, hasLabel ? "remove_label" : "add_label", {
          label_id: labelId,
        });
      } catch (error) {
        return setNotice(error.message);
      }
    setConversationLabels((current) => ({
      ...current,
      [id]: hasLabel
        ? current[id].filter((item) => item !== labelId)
        : [...(current[id] || []), labelId],
    }));
  }
  async function transferTo(user) {
    if (organizationId)
      try {
        await runWhatsAppAction(selected.id, "transfer", { user_id: user.id });
      } catch (error) {
        return setNotice(error.message);
      }
    updateConversation(selected.id, {
      owner: user.full_name,
      ownerId: user.id,
    });
    setTransferOpen(false);
    setNotice(`Atendimento transferido para ${user.full_name}.`);
  }
  function addMockAttachment(type) {
    if (!selected) return;
    const payload = {
      id: Date.now(),
      direction: "out",
      type,
      time: "agora",
      status: "sending",
      ...(type === "document"
        ? { name: "Documento-comercial.pdf", size: "1,2 MB" }
        : {}),
      ...(type === "audio" ? { duration: "0:24" } : {}),
      ...(type === "location"
        ? { text: "Localização compartilhada", address: "São Paulo — SP" }
        : {}),
    };
    setMessages((current) => ({
      ...current,
      [selected.id]: [...(current[selected.id] || []), payload],
    }));
    setAttachmentMenu(false);
  }
  async function createQuickOpportunity() {
    if (!selected || !requestedServices[selected.id] || !quickStageId) return;
    setQuickSaving(true);
    setQuickError("");
    try {
      const saved = await onCreateOpportunity({
        name: selected.name,
        phone: selected.phone,
        whatsapp_contact_id: selected.contactId || null,
        service_id: requestedServices[selected.id],
        service_type: services.find(service => service.id === requestedServices[selected.id])?.name || null,
        estimated_value: 0,
        source_name: "WhatsApp",
        owner_id: selectedOwner?.id || null,
        stage_id: quickStageId,
        priority: "medium",
      });
      setQuickOpportunities((current) => ({
        ...current,
        [selected.id]: saved,
      }));
      setQuickConfirm(false);
      setNotice("Oportunidade criada e adicionada ao pipeline.");
    } catch (error) {
      setQuickError(error.message || "Não foi possível criar a oportunidade.");
    } finally {
      setQuickSaving(false);
    }
  }
  function openContext(event, id) {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({
      id,
      x: Math.min(event.clientX, innerWidth - 230),
      y: Math.min(event.clientY, innerHeight - 330),
    });
  }
  async function copyMessage(message) {
    const content = message.text || message.name || message.address || "";
    try {
      await navigator.clipboard.writeText(content);
      setNotice("Mensagem copiada.");
    } catch {
      setNotice("Não foi possível copiar a mensagem.");
    }
    setMessageMenu(null);
  }
  async function reactToMessage(messageId, emoji) {
    if (organizationId)
      try {
        await runWhatsAppAction(selected.id, "react", {
          message_id: messageId,
          emoji,
        });
      } catch (error) {
        return setNotice(error.message);
      }
    setMessages((current) => ({
      ...current,
      [selected.id]: current[selected.id].map((message) =>
        message.id === messageId
          ? { ...message, reaction: message.reaction === emoji ? null : emoji }
          : message,
      ),
    }));
    setReactionPicker(null);
    setMessageMenu(null);
  }

  async function saveEditedMessage(event) {
    event?.preventDefault();
    const text = messageEdit?.text?.trim();
    if (!selected || !messageEdit || !text || messageSaving) return;
    setMessageSaving(true);
    try {
      if (organizationId) {
        await runWhatsAppAction(selected.id, "edit_message", {
          message_id: messageEdit.id,
          text,
        });
        const rows = await loadWhatsAppMessages(selected.id);
        setMessages((current) => ({ ...current, [selected.id]: rows }));
      } else {
        setMessages((current) => ({
          ...current,
          [selected.id]: (current[selected.id] || []).map((item) =>
            item.id === messageEdit.id
              ? { ...item, text, editedAt: new Date().toISOString() }
              : item,
          ),
        }));
      }
      setMessageEdit(null);
      setNotice("Mensagem editada.");
    } catch (error) {
      setNotice(error.message || "Não foi possível editar a mensagem.");
    } finally {
      setMessageSaving(false);
    }
  }

  async function deleteMessageForEveryone(message) {
    if (!selected || messageSaving) return;
    if (!window.confirm("Apagar esta mensagem para todos?")) return;
    setMessageSaving(true);
    setMessageMenu(null);
    try {
      if (organizationId) {
        await runWhatsAppAction(selected.id, "delete_message", {
          message_id: message.id,
        });
        const rows = await loadWhatsAppMessages(selected.id);
        setMessages((current) => ({ ...current, [selected.id]: rows }));
      } else {
        setMessages((current) => ({
          ...current,
          [selected.id]: (current[selected.id] || []).map((item) =>
            item.id === message.id
              ? { ...item, text: null, deletedAt: new Date().toISOString() }
              : item,
          ),
        }));
      }
      setNotice("Mensagem apagada para todos.");
    } catch (error) {
      setNotice(error.message || "Não foi possível apagar a mensagem.");
    } finally {
      setMessageSaving(false);
    }
  }

  function openQuickOpportunity(id) {
    const conversation = conversations.find((item) => item.id === id);
    if (!conversation) return;
    const lead = leads.find(
      (item) =>
        item.id === conversation.leadId ||
        digits(item.phone_e164 || item.phone) === digits(conversation.phone),
    );
    setSelectedId(id);
    setContextMenu(null);
    setHeaderMenu(false);
    if (lead) {
      onOpenLead?.(lead);
      return;
    }
    setQuickError("");
    setQuickStageId((current) => current || stages.find((stage) => stage.status === "open")?.id || stages[0]?.id || "");
    setQuickConfirm(true);
  }
  async function changeConversation(action, payload, optimistic) {
    if (organizationId)
      try {
        await runWhatsAppAction(selected.id, action, payload);
      } catch (error) {
        return setNotice(error.message);
      }
    updateConversation(selected.id, optimistic);
    setHeaderMenu(false);
  }
  async function changeRequestedService(value) {
    setRequestedServices((current) => ({ ...current, [selected.id]: value }));
    const service = services.find(item => item.id === value)
    if (organizationId)
      try {
        await runWhatsAppAction(selected.id, "service", {
          requested_service_id: service?.id || null,
          requested_service: service?.name || null,
        });
      } catch (error) {
        setNotice(error.message);
      }
  }
  function openMessageMenu(event, message) {
    if (event.target.closest("button")) return;
    event.stopPropagation();
    setMessageMenu({ id: message.id });
    setReactionPicker(null);
  }

  return (
    <section
      className={`service-page ${privacyMode ? "privacy-mode" : ""} mobile-${mobileView}-open`}
      onClickCapture={(event) => {
        if (contextMenu && !event.target.closest(".conversation-context-menu")) {
          event.preventDefault();
          event.stopPropagation();
          setContextMenu(null);
        }
      }}
      onClick={() => {
        setContextMenu(null);
        setHeaderMenu(false);
        setListMenu(false);
        setLabelFilterMenu(false);
        setMessageMenu(null);
        setReactionPicker(null);
      }}
    >
      <header className="page-header service-page-header">
        <div>
          <span className="eyebrow">Central de conversas</span>
          <h1>Atendimento</h1>
          <p>Organize conversas e acompanhe cada contato pelo WhatsApp.</p>
        </div>
        <button
          className={`connection-pill ${connection}`}
          onClick={(event) => {
            event.stopPropagation();
            if (!organizationId)
              setConnection(
                connection === "connected" ? "disconnected" : "connected",
              );
          }}
        >
          <span />
          {connection === "connected" ? <WhatsAppIcon /> : <WifiOff />}
          <strong>
            {connection === "connected"
              ? "WhatsApp conectado"
              : "WhatsApp desconectado"}
          </strong>
          <small>
            {organizationId
              ? "Estado sincronizado com o canal"
              : "Clique para simular o estado"}
          </small>
        </button>
      </header>
      <div
        className={`service-shell ${showContact ? "" : "contact-is-hidden"} mobile-${mobileView}-view`}
      >
        <aside className="conversation-pane">
          <div className="conversation-head">
            <div>
              <h2>{filter === "archived" ? "Arquivadas" : "Conversas"}</h2>
              <span>{unreadTotal} não lidas</span>
            </div>
            <div className="list-menu-wrap">
              <button
                className={`icon-button privacy-toggle ${privacyMode ? "active" : ""}`}
                title={privacyMode ? "Mostrar nomes e telefones" : "Ocultar nomes e telefones"}
                aria-label={privacyMode ? "Mostrar nomes e telefones" : "Ocultar nomes e telefones"}
                aria-pressed={privacyMode}
                onClick={(event) => {
                  event.stopPropagation();
                  setPrivacyMode(!privacyMode);
                }}
              >
                {privacyMode ? <Eye /> : <EyeOff />}
              </button>
              <button
                className="icon-button"
                onClick={(event) => {
                  event.stopPropagation();
                  setListMenu(!listMenu);
                }}
              >
                <MoreVertical />
              </button>
              {listMenu && (
                <div
                  className="header-dropdown list-dropdown"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button onClick={simulateIncoming}>
                    <MessageCircle />
                    Simular nova mensagem
                  </button>
                  <button onClick={() => setSoundEnabled(!soundEnabled)}>
                    {soundEnabled ? <BellOff /> : <Bell />}
                    {soundEnabled
                      ? "Silenciar notificações"
                      : "Ativar notificações"}
                  </button>
                  <button
                    onClick={() => showArchivedConversations(filter !== "archived")}
                  >
                    <Archive />
                    {filter === "archived"
                      ? "Voltar às conversas"
                      : "Ver arquivadas"}
                  </button>
                </div>
              )}
            </div>
          </div>
          <label className="conversation-search">
            <Search />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar conversa"
            />
          </label>
          {filter === "archived" ? (
            <button
              type="button"
              className="archived-conversations-back"
              onClick={() => showArchivedConversations(false)}
            >
              <ChevronLeft />
              <span>Voltar às conversas</span>
            </button>
          ) : archivedTotal > 0 ? (
            <button
              type="button"
              className="archived-conversations-entry"
              onClick={() => showArchivedConversations(true)}
            >
              <Archive />
              <span>Arquivadas</span>
              <strong>{archivedTotal}</strong>
              <ChevronRight />
            </button>
          ) : null}
          {filter !== "archived" && <div className="conversation-filters">
            {[
              ["all", "Todas"],
              ["unread", "Não lidas"],
              ["open", "Em atendimento"],
            ].map(([id, label]) => (
              <button
                key={id}
                className={filter === id ? "active" : ""}
                onClick={() => setFilter(id)}
              >
                {label}
              </button>
            ))}
            <div className="label-filter-wrap">
              <button
                className={
                  labelFilter !== "all"
                    ? "label-filter-button active"
                    : "label-filter-button"
                }
                onClick={(event) => {
                  event.stopPropagation();
                  setLabelFilterMenu(!labelFilterMenu);
                }}
              >
                <Tag />
                {labelFilter === "all"
                  ? "Etiquetas"
                  : labels.find((item) => item.id === labelFilter)?.name}
                <ChevronDown />
              </button>
              {labelFilterMenu && (
                <div
                  className="label-filter-dropdown"
                  onClick={(event) => event.stopPropagation()}
                >
                  <button
                    className={labelFilter === "all" ? "selected" : ""}
                    onClick={() => {
                      setLabelFilter("all");
                      setLabelFilterMenu(false);
                    }}
                  >
                    Todas as etiquetas
                  </button>
                  {labels.map((label) => (
                    <button
                      key={label.id}
                      className={labelFilter === label.id ? "selected" : ""}
                      onClick={() => {
                        setLabelFilter(label.id);
                        setLabelFilterMenu(false);
                      }}
                    >
                      <i style={{ background: label.color }} />
                      {label.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>}
          <div className="conversation-list">
            {visible.map((item) => {
              const commercial = conversationCommercialMeta(item);
              return (
              <button
                key={item.id}
                className={`conversation-item ${selected?.id === item.id ? "active" : ""}`}
                onClick={() => selectConversation(item.id)}
                onContextMenu={(event) => openContext(event, item.id)}
              >
                  <ContactAvatar contact={item} className="service-avatar" />
                <span className="conversation-copy">
                  <span>
                    <strong className="privacy-sensitive conversation-desktop-name">{item.name}</strong>
                    <strong className="privacy-sensitive conversation-mobile-name">{commercial.firstName}</strong>
                    <time>{item.time}</time>
                  </span>
                  <small>{item.preview}</small>
                  <em>
                    {item.owner}
                    {item.muted ? " · silenciada" : ""}
                  </em>
                  <span className="conversation-mobile-meta">
                    <span>{commercial.source}</span>
                    <i>•</i>
                    <strong>{commercial.stage}</strong>
                  </span>
                  {conversationLabels[item.id]?.length > 0 && (
                    <span className="conversation-tags">
                      {conversationLabels[item.id].map((id) => {
                        const label = labels.find((item) => item.id === id);
                        return (
                          label && (
                            <i key={id} style={{ "--tag": label.color }}>
                              {label.name}
                            </i>
                          )
                        );
                      })}
                    </span>
                  )}
                </span>
                {item.archived ? (
                  <span
                    className="conversation-restore"
                    role="button"
                    tabIndex="0"
                    title="Desarquivar conversa"
                    aria-label={`Desarquivar conversa com ${item.name}`}
                    onClick={(event) => {
                      event.stopPropagation();
                      archive(item.id, false);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        event.stopPropagation();
                        archive(item.id, false);
                      }
                    }}
                  >
                    <RotateCcw />
                  </span>
                ) : item.unread > 0 ? <b>{item.unread}</b> : null}
                <span
                  className="conversation-more"
                  onClick={(event) => openContext(event, item.id)}
                >
                  <MoreVertical />
                </span>
              </button>
              );
            })}
            {!visible.length && (
              <div className="conversation-empty">
                <Inbox />
                <span>Nenhuma conversa encontrada</span>
              </div>
            )}
          </div>
        </aside>
        <article className="chat-pane" style={{ position: "relative", display: "block", height: "auto", minHeight: 0, alignSelf: "stretch", overflow: "hidden" }}>
          {selected ? (
            <>
              <header className="chat-head">
              <button
                type="button"
                className="icon-button mobile-chat-back"
                aria-label="Voltar para conversas"
                onClick={() => setMobileView("list")}
              >
                <ChevronLeft />
              </button>
              <ContactAvatar contact={selected} />
                <div>
                  <strong className="privacy-sensitive">{selected.name}</strong>
                  <small>
                    <span className="privacy-sensitive">{selected.phone}</span> ·{" "}
                    {selected.online ? "online" : "visto recentemente"}
                  </small>
                </div>
                <div className="chat-menu-wrap">
                  <button
                    className="icon-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setHeaderMenu(!headerMenu);
                    }}
                  >
                    <MoreVertical />
                  </button>
                  {headerMenu && (
                    <div
                      className="header-dropdown"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          setShowContact(!showContact);
                          setHeaderMenu(false);
                        }}
                      >
                        <UserRound />
                        {showContact
                          ? "Ocultar informações"
                          : "Mostrar informações"}
                      </button>
                      <button onClick={() => openQuickOpportunity(selected.id)}>
                        <Plus />
                        {linkedLead ? "Abrir oportunidade" : "Criar oportunidade rápida"}
                      </button>
                      {linkedLead && <button onClick={() => { onNewOpportunity?.({ ...linkedLead, contactId: selected.contactId }); setHeaderMenu(false); }}><Plus />Nova oportunidade para este contato</button>}
                      <div className="header-labels context-labels">
                        <span>Etiquetas</span>
                        {labels.length ? labels.map((label) => (
                          <button
                            type="button"
                            key={label.id}
                            onClick={() => toggleConversationLabel(selected.id, label.id)}
                          >
                            <i
                              className={(conversationLabels[selected.id] || []).includes(label.id) ? "selected" : ""}
                              style={{ "--label": label.color }}
                            />
                            {label.name}
                          </button>
                        )) : <small className="menu-empty">Nenhuma etiqueta cadastrada</small>}
                      </div>
                      <button
                        onClick={() => {
                          updateConversation(selected.id, {
                            unread: selected.unread ? 0 : 1,
                          });
                          setHeaderMenu(false);
                        }}
                      >
                        <MessageCircle />
                        {selected.unread
                          ? "Marcar como lida"
                          : "Marcar como não lida"}
                      </button>
                      <button
                        onClick={() => {
                          updateConversation(selected.id, {
                            muted: !selected.muted,
                          });
                          setHeaderMenu(false);
                        }}
                      >
                        {selected.muted ? <Bell /> : <BellOff />}
                        {selected.muted
                          ? "Ativar notificações"
                          : "Silenciar conversa"}
                      </button>
                      <button
                        onClick={() => {
                          updateConversation(selected.id, {
                            status:
                              selected.status === "resolved"
                                ? "open"
                                : "resolved",
                          });
                          setHeaderMenu(false);
                        }}
                      >
                        {selected.status === "resolved" ? (
                          <RotateCcw />
                        ) : (
                          <Check />
                        )}
                        {selected.status === "resolved"
                          ? "Reabrir atendimento"
                          : "Finalizar atendimento"}
                      </button>
                      <button
                        onClick={() => archive(selected.id, !selected.archived)}
                      >
                        <Archive />
                        {selected.archived
                          ? "Restaurar conversa"
                          : "Arquivar conversa"}
                      </button>
                    </div>
                  )}
                </div>
              </header>
              <div ref={messageAreaRef} className="message-area" style={{ position: "absolute", zIndex: 1, top: 70, right: 0, bottom: 65, left: 0, minHeight: 0, overflowY: "auto", overflowX: "hidden" }}>
                <div className="day-divider">
                  <span>Hoje</span>
                </div>
                {(messages[selected.id] || []).map((message) => (
                  <div
                    key={message.id}
                    className={`message-row ${message.direction}`}
                  >
                    <div
                      className="message-bubble"
                      onClick={(event) => openMessageMenu(event, message)}
                      onContextMenu={(event) => {
                        event.preventDefault();
                        openMessageMenu(event, message);
                      }}
                    >
                      <button
                        type="button"
                        className="message-action-trigger"
                        aria-label="Ações da mensagem"
                        onClick={(event) => {
                          event.stopPropagation();
                          setMessageMenu(
                            messageMenu?.id === message.id
                              ? null
                              : { id: message.id },
                          );
                          setReactionPicker(null);
                        }}
                      >
                        <ChevronDown />
                      </button>
                      <MessageContent
                        message={message}
                        onPreview={setMediaPreview}
                      />
                      <span>
                        {message.time}
                        {message.direction === "out" && (
                          <MessageStatus status={message.status} />
                        )}
                      </span>
                      {message.reaction && (
                        <b className="message-reaction">{message.reaction}</b>
                      )}
                      {messageMenu?.id === message.id && (
                        <div
                          className="message-actions-menu"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setReplyTo(message);
                              setMessageMenu(null);
                            }}
                          >
                            <Reply />
                            Responder
                          </button>
                          <button
                            type="button"
                            onClick={() => copyMessage(message)}
                          >
                            <Copy />
                            Copiar
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setReactionPicker(message.id);
                              setMessageMenu(null);
                            }}
                          >
                            <Smile />
                            Reagir
                          </button>
                          {message.direction === "out" && message.type === "text" && !message.deletedAt && message.external_message_id && (
                            <button
                              type="button"
                              onClick={() => {
                                setMessageEdit({ id: message.id, text: message.text || "" });
                                setMessageMenu(null);
                              }}
                            >
                              <Pencil />
                              Editar
                            </button>
                          )}
                          {message.direction === "out" && !message.deletedAt && message.external_message_id && (
                            <button
                              type="button"
                              className="danger-action"
                              onClick={() => deleteMessageForEveryone(message)}
                            >
                              <Trash2 />
                              Apagar para todos
                            </button>
                          )}
                        </div>
                      )}
                      {reactionPicker === message.id && (
                        <div
                          className="reaction-picker"
                          onClick={(event) => event.stopPropagation()}
                        >
                          {["👍", "❤️", "😂", "😮", "😢", "🙏"].map((emoji) => (
                            <button
                              type="button"
                              key={emoji}
                              onClick={() => reactToMessage(message.id, emoji)}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {typing && (
                  <div className="typing-indicator">
                    <span />
                    <span />
                    <span />
                    <small><span className="privacy-sensitive">{selected.name}</span> está digitando</small>
                  </div>
                )}
              </div>
              <form className="message-composer" onSubmit={sendMessage} style={{ position: "absolute", zIndex: 50, display: "grid", visibility: "visible", opacity: 1, right: 0, bottom: 0, left: 0, width: "100%", minHeight: 65, margin: 0, background: "#111720" }}>
                {replyTo && (
                  <div className="reply-composer-preview">
                    <Reply />
                    <div>
                      <strong>
                        Respondendo a{" "}
                        {replyTo.direction === "out" ? "você" : <span className="privacy-sensitive">{selected.name}</span>}
                      </strong>
                      <span>
                        {replyTo.text ||
                          replyTo.name ||
                          replyTo.address ||
                          "Mensagem"}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => setReplyTo(null)}
                      aria-label="Cancelar resposta"
                    >
                      <X />
                    </button>
                  </div>
                )}
                <div className="attachment-wrap">
                  <button
                    type="button"
                    className="composer-action"
                    onClick={() => setAttachmentMenu(!attachmentMenu)}
                  >
                    <Paperclip />
                  </button>
                  {attachmentMenu && (
                    <div className="attachment-menu">
                      {[
                        ["image", Image, "Imagem"],
                        ["document", FileText, "Documento"],
                        ["audio", Mic, "Áudio"],
                        ["video", Video, "Vídeo"],
                        ["location", MapPin, "Localização"],
                      ].map(([type, Icon, label]) => (
                        <button
                          type="button"
                          key={type}
                          onClick={() => addMockAttachment(type)}
                        >
                          <Icon />
                          {label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <textarea
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey)
                      sendMessage(event);
                  }}
                  placeholder={
                    connection === "connected"
                      ? "Digite uma mensagem"
                      : "WhatsApp desconectado"
                  }
                  disabled={connection !== "connected"}
                  rows="1"
                />
                <button
                  className="send-button"
                  disabled={!draft.trim() || connection !== "connected"}
                >
                  <Send />
                </button>
              </form>
            </>
          ) : (
            <div className="no-conversation-selected">
              <MessageCircle />
              <h2>Selecione uma conversa</h2>
              <p>Escolha um contato ao lado para iniciar o atendimento.</p>
            </div>
          )}
        </article>
        {showContact && selected && (
          <aside className="contact-pane">
            <div className="contact-profile">
            <ContactAvatar contact={selected} className="contact-avatar" />
              <h2 className="privacy-sensitive">{selected.name}</h2>
              <a
                className="privacy-sensitive"
                href={`https://wa.me/${digits(selected.phone)}`}
                target="_blank"
                rel="noreferrer"
              >
                <WhatsAppIcon />
                {selected.phone}
              </a>
            </div>
            <div className="contact-section">
              <span className="contact-label">Atendimento</span>
              <dl>
                <div>
                  <dt>Situação</dt>
                  <dd>
                    <span className="service-status">
                      {selected.status === "resolved"
                        ? "Finalizado"
                        : selected.status === "waiting"
                          ? "Aguardando resposta"
                          : "Em atendimento"}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt>Responsável</dt>
                  <dd>{selected.owner}</dd>
                </div>
                <div>
                  <dt>Canal</dt>
                  <dd>WhatsApp</dd>
                </div>
              </dl>
              <label className="requested-service">
                Serviço solicitado
                <select
                  value={requestedServices[selected.id] || ""}
                  onChange={(event) => changeRequestedService(event.target.value)}
                >
                  <option value="">Selecione um serviço</option>
                  {services.filter(service => service.active || service.id === requestedServices[selected.id]).map((service) => (
                    <option value={service.id} key={service.id}>{service.name}</option>
                  ))}
                </select>
              </label>
            </div>
            <div className="contact-section">
              <span className="contact-label">Oportunidade</span>
              {opportunity ? (
                <>
                  <div className="linked-lead">
                    <span className="avatar small">
                      {initials(opportunity.name)}
                    </span>
                    <div>
                      <strong className="privacy-sensitive">{opportunity.name}</strong>
                      <small>
                        {opportunity.service_type || "Serviço não informado"}
                      </small>
                    </div>
                  </div>
                  <dl>
                    <div>
                      <dt>Valor estimado</dt>
                      <dd>{money(opportunity.estimated_value)}</dd>
                    </div>
                    <div>
                      <dt>Origem</dt>
                      <dd>{opportunity.source_name || "WhatsApp"}</dd>
                    </div>
                  </dl>
                  <div className="contact-relationship-summary">
                    <span><b>{relatedLeads.length}</b> oportunidades</span>
                    <span><b>{relationshipOpen}</b> em aberto</span>
                    <span><b>{money(relationshipEstimated)}</b> estimado</span>
                    <span><b>{money(relationshipWon)}</b> ganho</span>
                  </div>
                  {opportunity.demo && (
                    <p className="demo-opportunity-note">
                      Oportunidade criada apenas nesta demonstração visual.
                    </p>
                  )}
                  <button
                    className="secondary contact-action"
                    onClick={() => linkedLead && onOpenLead(linkedLead)}
                    disabled={!linkedLead}
                  >
                    Abrir oportunidade
                  </button>
                  <button className="quick-opportunity contact-action" onClick={() => onNewOpportunity?.({ ...opportunity, contactId: selected.contactId })}><Plus />Nova oportunidade</button>
                </>
              ) : (
                <div className="no-linked-lead">
                  <p>
                    Selecione o serviço e cadastre este contato rapidamente no
                    CRM.
                  </p>
                  <button
                    className="quick-opportunity"
                    onClick={() => {
                      setQuickStageId(stages[0]?.id || "");
                      setQuickError("");
                      setQuickConfirm(true);
                    }}
                    disabled={!requestedServices[selected.id]}
                  >
                    <Plus />
                    Criar oportunidade rápida
                  </button>
                </div>
              )}
            </div>
            <div className="contact-actions">
              <button onClick={() => setTransferOpen(true)}>
                <Users />
                Transferir
              </button>
              <button onClick={() => archive(selected.id, !selected.archived)}>
                <Archive />
                {selected.archived ? "Restaurar" : "Arquivar"}
              </button>
            </div>
          </aside>
        )}
      </div>

      {contextMenu && (
        <div
          className="conversation-context-menu extended"
          style={{ left: contextMenu.x, top: contextMenu.y }}
          onClick={(event) => event.stopPropagation()}
        >
          <button onClick={() => openQuickOpportunity(contextMenu.id)}>
            <Plus />
            {leads.some((lead) => {
              const conversation = conversations.find((item) => item.id === contextMenu.id);
              return conversation && (lead.id === conversation.leadId || digits(lead.phone_e164 || lead.phone) === digits(conversation.phone));
            }) ? "Abrir oportunidade" : "Criar oportunidade rápida"}
          </button>
          <button
            onClick={() => {
              const item = conversations.find(
                (row) => row.id === contextMenu.id,
              );
              updateConversation(contextMenu.id, {
                unread: item?.unread ? 0 : 1,
              });
              setContextMenu(null);
            }}
          >
            <MessageCircle />
            {conversations.find((item) => item.id === contextMenu.id)?.unread
              ? "Marcar como lida"
              : "Marcar como não lida"}
          </button>
          <button
            onClick={() =>
              archive(
                contextMenu.id,
                !conversations.find((item) => item.id === contextMenu.id)
                  ?.archived,
              )
            }
          >
            <Archive />
            {conversations.find((item) => item.id === contextMenu.id)?.archived
              ? "Restaurar conversa"
              : "Arquivar conversa"}
          </button>
          <div className="context-labels">
            <span>Adicionar etiqueta</span>
            {labels.map((label) => (
              <button
                key={label.id}
                onClick={() =>
                  toggleConversationLabel(contextMenu.id, label.id)
                }
              >
                <i
                  className={
                    (conversationLabels[contextMenu.id] || []).includes(
                      label.id,
                    )
                      ? "selected"
                      : ""
                  }
                  style={{ "--label": label.color }}
                />
                {label.name}
              </button>
            ))}
          </div>
        </div>
      )}
      {notice && <div className="service-notice">{notice}</div>}
      {transferOpen && selected && (
        <div className="mini-modal-backdrop">
          <section className="mini-modal">
            <header>
              <div>
                <span className="eyebrow">Responsável</span>
                <h2>Transferir atendimento</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setTransferOpen(false)}
              >
                <X />
              </button>
            </header>
            <div className="transfer-list">
              {users.map((user) => (
                <button key={user.id} onClick={() => transferTo(user)}>
                  <span className="avatar small">
                    {initials(user.full_name)}
                  </span>
                  <span>
                    <strong>{user.full_name}</strong>
                    <small>{user.role}</small>
                  </span>
                  {selected.owner === user.full_name && <Check />}
                </button>
              ))}
            </div>
          </section>
        </div>
      )}
      {quickConfirm && selected && (
        <div className="mini-modal-backdrop">
          <section className="mini-modal quick-confirm">
            <header>
              <div>
                <span className="eyebrow">Oportunidade rápida</span>
                <h2>Confirmar cadastro</h2>
              </div>
              <button
                className="icon-button"
                onClick={() => setQuickConfirm(false)}
              >
                <X />
              </button>
            </header>
            <dl>
              <div>
                <dt>Nome</dt>
                <dd className="privacy-sensitive">{selected.name}</dd>
              </div>
              <div>
                <dt>Telefone</dt>
                <dd className="privacy-sensitive">{selected.phone}</dd>
              </div>
              <div>
                <dt>Serviço</dt>
                <dd>{services.find(service => service.id === requestedServices[selected.id])?.name || 'Não informado'}</dd>
              </div>
              <div>
                <dt>Origem</dt>
                <dd>WhatsApp</dd>
              </div>
              <div>
                <dt>Responsável</dt>
                <dd>{selected.owner}</dd>
              </div>
            </dl>
            <label className="quick-stage-field">
              Serviço solicitado
              <select
                value={requestedServices[selected.id] || ""}
                onChange={(event) => setRequestedServices((current) => ({ ...current, [selected.id]: event.target.value }))}
                required
              >
                <option value="">Selecione um serviço</option>
                {services.map((service) => (
                  <option value={service.id} key={service.id}>{service.name}</option>
                ))}
              </select>
            </label>
            <label className="quick-stage-field">
              Fase da pipeline
              <select
                value={quickStageId}
                onChange={(event) => setQuickStageId(event.target.value)}
                required
              >
                <option value="">Selecione uma fase</option>
                {stages.map((stage) => (
                  <option value={stage.id} key={stage.id}>
                    {stage.name}
                  </option>
                ))}
              </select>
            </label>
            {quickError && <p className="quick-error">{quickError}</p>}
            <footer>
              <button
                className="secondary"
                onClick={() => setQuickConfirm(false)}
              >
                Cancelar
              </button>
              <button
                className="primary"
                onClick={createQuickOpportunity}
                disabled={!requestedServices[selected.id] || !quickStageId || quickSaving}
              >
                {quickSaving ? "Criando…" : "Criar oportunidade"}
              </button>
            </footer>
          </section>
        </div>
      )}
      {messageEdit && (
        <div className="mini-modal-backdrop">
          <form className="mini-modal message-edit-modal" onSubmit={saveEditedMessage}>
            <header>
              <div>
                <span className="eyebrow">Mensagem enviada</span>
                <h2>Editar mensagem</h2>
              </div>
              <button type="button" className="icon-button" onClick={() => setMessageEdit(null)}>
                <X />
              </button>
            </header>
            <label>
              Novo texto
              <textarea
                autoFocus
                value={messageEdit.text}
                onChange={(event) => setMessageEdit((current) => ({ ...current, text: event.target.value }))}
                maxLength={4096}
                rows={5}
              />
            </label>
            <footer>
              <button type="button" className="secondary" onClick={() => setMessageEdit(null)}>Cancelar</button>
              <button type="submit" className="primary" disabled={!messageEdit.text.trim() || messageSaving}>
                {messageSaving ? "Salvando…" : "Salvar alteração"}
              </button>
            </footer>
          </form>
        </div>
      )}
      {mediaPreview && (
        <div className="mini-modal-backdrop">
          <section className="media-preview">
            <button
              className="icon-button"
              onClick={() => setMediaPreview(null)}
            >
              <X />
            </button>
            {mediaPreview.mediaUrl && mediaPreview.type === "image" ? (
              <img className="media-preview-image" src={mediaPreview.mediaUrl} alt={mediaPreview.name || "Imagem"} />
            ) : mediaPreview.mediaUrl && mediaPreview.type === "video" ? (
              <video className="media-preview-video" controls autoPlay src={mediaPreview.mediaUrl} />
            ) : mediaPreview.type === "document" ? (
              <FileText />
            ) : mediaPreview.type === "video" ? (
              <Video />
            ) : (
              <Image />
            )}
            <h2>
              {mediaPreview.name ||
                (mediaPreview.type === "video"
                  ? "Vídeo"
                  : "Imagem")}
            </h2>
            {mediaPreview.mediaUrl ? (
              <a className="secondary" href={mediaPreview.mediaUrl} target="_blank" rel="noreferrer" download={mediaPreview.name}><Download />Baixar arquivo</a>
            ) : (
              <p>Arquivo de mídia indisponível.</p>
            )}
          </section>
        </div>
      )}
    </section>
  );
}
